import React from 'react'
import LiveModal from 'live/components/ui/Modal'
import i18n from 'live/services/i18n'
import styled from 'styled-components'
import { ProgressBar } from '@intelie/ui'
import utils from 'live/lib/utils'
import LiveTooltip from 'live/components/ui/tooltip'

type Props = {
    currentEvent: PurgeEvent
    closeDetails: () => void
}

const PurgeDetails = ({ currentEvent, closeDetails }: Props): JSX.Element => {
    return (
        <LiveModal className="modal-sm" show={true} onHide={closeDetails}>
            <LiveModal.Header>
                <LiveModal.Title>{i18n('Purge details')}</LiveModal.Title>
            </LiveModal.Header>
            <ProgressBarContainer>
                <ProgressBar now={(currentEvent.progress / currentEvent.total) * 100} />
            </ProgressBarContainer>
            <LiveModal.Body>
                <p>
                    <InfoDetail>{i18n('Purge Id')}: </InfoDetail>
                    {currentEvent.purgeId}
                </p>
                <p>
                    <InfoDetail>{i18n('Simulation')}: </InfoDetail>
                    {currentEvent.simulation ? i18n('Yes') : i18n('No')}
                </p>
                <p>
                    <InfoDetail>{i18n('Elapsed time')}: </InfoDetail>
                    {utils.millisecondsToTime(currentEvent.duration, 0)}
                </p>
                <p>
                    <InfoDetail>{i18n('Rules executed')}: </InfoDetail>
                    {currentEvent.progress}/{currentEvent.total}
                </p>
                <p>
                    <InfoDetail>{i18n('Total events')}: </InfoDetail>
                    {utils.formatNumber(currentEvent.eventCount)}
                </p>
                <p>
                    <InfoDetail>{i18n('Estimated space saving')}: </InfoDetail>
                    {utils.bytesToSize(currentEvent.bytes)}{' '}
                    <LiveTooltip position="top" content={i18n('Message about estimated space saving')}>
                        <i className="fa fa-info-circle" />
                    </LiveTooltip>
                </p>
                <p>
                    <InfoDetail>{i18n('Rule details')}: </InfoDetail>
                </p>
                {currentEvent.definition.rules.map(rule => {
                    return (
                        <div style={{ marginBottom: '8px' }} key={`${rule.eventType}-${rule.timeRange}`}>
                            <p style={{ margin: '0px', wordBreak: 'break-all' }}>{rule.eventType}</p>
                            <p style={{ fontStyle: 'italic', margin: '0px' }}>{rule.timeRange}</p>
                            <p>
                                <span style={{ fontSize: '11px' }}>
                                    {i18n('Status')}
                                    {': '}
                                    {rule.current ? (
                                        <LiveTooltip position="top" content={i18n('Executing')}>
                                            <i className={'fa fa-refresh'} />
                                        </LiveTooltip>
                                    ) : rule.duration ? (
                                        <LiveTooltip position="top" content={i18n('Executed')}>
                                            <i className={'fa fa-check-circle-o'} />
                                        </LiveTooltip>
                                    ) : (
                                        <LiveTooltip position="top" content={i18n('Awaiting execution')}>
                                            <i className={'fa fa-clock-o'} />
                                        </LiveTooltip>
                                    )}
                                </span>
                                {rule.duration !== undefined && (
                                    <span style={{ margin: '0px', fontSize: '11px' }}>
                                        {' | '}
                                        {i18n('Duration')}: {utils.millisecondsToTime(rule.duration, 0)}
                                    </span>
                                )}
                                {rule.eventCount !== undefined && (
                                    <span style={{ margin: '0px', fontSize: '11px' }}>
                                        {' | '}
                                        {i18n('Events')}: {utils.formatNumber(rule.eventCount)}
                                    </span>
                                )}
                            </p>
                        </div>
                    )
                })}
                <p>
                    <InfoDetail>{i18n('Exception details')}: </InfoDetail>
                </p>
                {currentEvent.definition.exceptions?.map(rule => {
                    return (
                        <div style={{ marginBottom: '8px' }} key={rule.eventType}>
                            <p style={{ margin: '0px' }}>{rule.eventType}</p>
                        </div>
                    )
                }) || i18n('None')}
            </LiveModal.Body>
            <LiveModal.Footer>
                <a className="btn btn-primary" onClick={closeDetails} style={{ padding: '6px 16px' }}>
                    {i18n('Ok')}
                </a>
            </LiveModal.Footer>
        </LiveModal>
    )
}

const ProgressBarContainer = styled.div`
    height: 1px;
    div {
        margin: 0px;
        background: var(--gray-300);
        border-radius: 0px;
    }
    span {
        background: var(--red-700);
        border-radius: 0px;
    }
`

const InfoDetail = styled.span`
    font-weight: 600;
`

const InfoMessage = styled.div`
    padding: 0;
    margin-top: 10px;
    background-color: transparent;
    i {
        margin-right: 5px;
    }
`

export default PurgeDetails
